import React, { useState, useEffect } from 'react';
import './OperatingHours.css';

const OperatingHours = ({ value, onChange, disabled = false }) => {
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Initialize state from value prop
  const [operatingHours, setOperatingHours] = useState(() => {
    if (value && typeof value === 'object') {
      return value;
    }
    // Default structure
    return {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    };
  });

  // Update state when value prop changes
  useEffect(() => {
    if (value && typeof value === 'object') {
      setOperatingHours(value);
    }
  }, [value]);

  // Check if a day is enabled (has at least one time slot)
  const isDayEnabled = (dayKey) => {
    return operatingHours[dayKey] && operatingHours[dayKey].length > 0;
  };

  // Toggle day on/off
  const toggleDay = (dayKey) => {
    const newHours = { ...operatingHours };
    if (isDayEnabled(dayKey)) {
      // Disable: remove all time slots
      newHours[dayKey] = [];
    } else {
      // Enable: add default time slot
      newHours[dayKey] = [{ open: '09:00', close: '17:00' }];
    }
    setOperatingHours(newHours);
    onChange(newHours);
  };

  // Add time slot for a day
  const addTimeSlot = (dayKey) => {
    const newHours = { ...operatingHours };
    if (!newHours[dayKey]) {
      newHours[dayKey] = [];
    }
    newHours[dayKey].push({ open: '09:00', close: '17:00' });
    setOperatingHours(newHours);
    onChange(newHours);
  };

  // Remove time slot for a day
  const removeTimeSlot = (dayKey, index) => {
    const newHours = { ...operatingHours };
    if (newHours[dayKey] && newHours[dayKey].length > index) {
      newHours[dayKey].splice(index, 1);
      setOperatingHours(newHours);
      onChange(newHours);
    }
  };

  // Update time slot
  const updateTimeSlot = (dayKey, index, field, time) => {
    const newHours = { ...operatingHours };
    if (newHours[dayKey] && newHours[dayKey][index]) {
      newHours[dayKey][index][field] = time;
      setOperatingHours(newHours);
      onChange(newHours);
    }
  };

  // Format time for display (HH:MM)
  const formatTime = (time) => {
    if (!time) return '00:00';
    // Handle various time formats
    if (time.includes(':')) {
      return time.substring(0, 5); // Take HH:MM part
    }
    return '00:00';
  };

  // Parse time input (kept for potential future use)
  // const parseTime = (input) => {
  //   // Remove non-numeric characters except colon
  //   const cleaned = input.replace(/[^\d:]/g, '');
  //   // Ensure format HH:MM
  //   if (cleaned.length === 4 && !cleaned.includes(':')) {
  //     return cleaned.substring(0, 2) + ':' + cleaned.substring(2, 4);
  //   }
  //   return cleaned.substring(0, 5);
  // };

  return (
    <div className="operating-hours-container">
      <div className="operating-hours-header">
        <h3>🕐 Operating Hours</h3>
        <p className="help-text">Configure operating times for each day of the week</p>
      </div>

      <div className="operating-hours-table">
        <div className="oh-table-header">
          <div className="oh-col-weekday">Weekday</div>
          <div className="oh-col-time">Open Time</div>
          <div className="oh-col-time">Close Time</div>
          <div className="oh-col-actions">Actions</div>
        </div>

        {days.map((day) => {
          const isEnabled = isDayEnabled(day.key);
          const timeSlots = operatingHours[day.key] || [];

          return (
            <div key={day.key} className={`oh-day-row ${isEnabled ? 'enabled' : 'disabled'}`}>
              {/* Weekday Column */}
              <div className="oh-col-weekday">
                <div className="oh-day-toggle">
                  <label className="oh-toggle-switch">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => !disabled && toggleDay(day.key)}
                      disabled={disabled}
                    />
                    <span className="oh-toggle-slider"></span>
                  </label>
                  <span className="oh-day-label">{day.label}</span>
                </div>
              </div>

              {/* Time Slots */}
              <div className="oh-time-slots">
                {isEnabled && timeSlots.length > 0 ? (
                  timeSlots.map((slot, index) => (
                    <div key={index} className="oh-time-slot-row">
                      <div className="oh-col-time">
                        <input
                          type="time"
                          value={formatTime(slot.open)}
                          onChange={(e) => !disabled && updateTimeSlot(day.key, index, 'open', e.target.value)}
                          disabled={disabled}
                          className="oh-time-input"
                        />
                      </div>
                      <div className="oh-col-time">
                        <input
                          type="time"
                          value={formatTime(slot.close)}
                          onChange={(e) => !disabled && updateTimeSlot(day.key, index, 'close', e.target.value)}
                          disabled={disabled}
                          className="oh-time-input"
                        />
                      </div>
                      <div className="oh-col-actions">
                        <button
                          type="button"
                          onClick={() => !disabled && addTimeSlot(day.key)}
                          disabled={disabled}
                          className="oh-btn-add"
                          title="Add another time slot"
                        >
                          <span>+</span>
                        </button>
                        {timeSlots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => !disabled && removeTimeSlot(day.key, index)}
                            disabled={disabled}
                            className="oh-btn-remove"
                            title="Remove this time slot"
                          >
                            <span>−</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="oh-time-slot-row">
                    <div className="oh-col-time">
                      <input type="time" disabled className="oh-time-input" />
                    </div>
                    <div className="oh-col-time">
                      <input type="time" disabled className="oh-time-input" />
                    </div>
                    <div className="oh-col-actions"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OperatingHours;





